import { useEffect, useState, useCallback } from "react";
import { Play, Square, Plus, Trash2, Timer } from "lucide-react";
import api from "../lib/api";

const fmt = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h ${m} m` : `${m} m`;
};

export const TimeTracker = ({ task }) => {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState("");
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/tasks/${task.id}/time`); setData(data); } catch {}
  }, [task.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.active_timer) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [data?.active_timer]);

  const start = async () => { await api.post(`/tasks/${task.id}/timer/start`); load(); };
  const stop = async () => { await api.post(`/timer/stop`); load(); };
  const addManual = async (e) => {
    e.preventDefault();
    const h = parseFloat(hours);
    if (!h || h <= 0) return;
    await api.post(`/tasks/${task.id}/time`, { hours: h, note: "Manual" });
    setHours(""); load();
  };
  const remove = async (id) => { await api.delete(`/time/${id}`); load(); };

  if (!data) return null;
  const running = data.active_timer;
  const runningSecs = running ? Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000) : 0;
  const total = data.total_seconds + runningSecs;
  const estimate = task.estimate_hours ? task.estimate_hours * 3600 : null;
  const pct = estimate ? Math.min(100, Math.round((total / estimate) * 100)) : null;

  return (
    <div data-testid="time-tracker">
      <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5"><Timer className="h-3 w-3" /> Control de tiempo</label>
      <div className="mt-2 bg-[#0A0A0B] rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-lg font-bold font-display" data-testid="time-total">{fmt(total)}{tick >= 0 && running ? " ⏱" : ""}</p>
            <p className="text-xs text-zinc-500">{estimate ? `de ${task.estimate_hours} h estimadas` : "sin estimación"}</p>
          </div>
          {running ? (
            <button onClick={stop} data-testid="timer-stop" className="flex items-center gap-1.5 bg-red-500/15 text-red-400 hover:bg-red-500/25 text-sm font-semibold px-3 py-2 rounded-lg transition-colors">
              <Square className="h-4 w-4" /> Detener
            </button>
          ) : (
            <button onClick={start} data-testid="timer-start" className="flex items-center gap-1.5 bg-[#00E676]/15 text-[#00E676] hover:bg-[#00E676]/25 text-sm font-semibold px-3 py-2 rounded-lg transition-colors">
              <Play className="h-4 w-4" /> Iniciar
            </button>
          )}
        </div>
        {pct !== null && (
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${pct >= 100 ? "bg-red-500" : "bg-[#00E676]"}`} style={{ width: `${pct}%` }} />
          </div>
        )}
        {data.entries.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {data.entries.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs text-zinc-400 group">
                <span className="font-semibold text-zinc-300 w-16">{fmt(e.seconds)}</span>
                <span className="flex-1 truncate">{e.user?.name} · {e.note}</span>
                <span className="text-zinc-600">{new Date(e.created_at).toLocaleDateString("es", { day: "numeric", month: "short" })}</span>
                <button onClick={() => remove(e.id)} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={addManual} className="flex gap-2">
          <input data-testid="manual-hours-input" type="number" step="0.25" min="0.25" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Horas (ej. 1.5)"
            className="flex-1 bg-[#1C1C1F] border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary" />
          <button type="submit" data-testid="manual-hours-add" className="bg-white/5 hover:bg-white/10 rounded-lg px-3 text-xs flex items-center gap-1"><Plus className="h-3 w-3" /> Registrar</button>
        </form>
      </div>
    </div>
  );
};
